import React, { useState } from "react";

import { Alert, AlertColor, Grid, Avatar, Button } from "@mui/material";
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';

import {
  FormInput as formInput,
  CustomInput,
} from "../../Components/Fields/FormToolBox";

declare global {
  interface Window {
    grecaptcha: any;
    electron: {
      language(): string;
      ipcRenderer: {
        sendMessage(channel: string, args: unknown[]): void;
      };
    };
  }
}


function Copyright(props: any) {
  return (
    <Typography variant="body2" color="text.secondary" align="center" {...props}>
      {'Copyright © '}
      <Link color="inherit" href="/">
        Open Rehab Initiative
      </Link>{' '}
      {new Date().getFullYear()}
      {'.'}
    </Typography>
  );
}

const defaultLoginButtonParams = {
  icon: "none",
  text: "Login",
};
interface FeedbackParams {
  icon: AlertColor | "none";
  text: string;
}
const defaultFeedbackParams: FeedbackParams = {
  icon: "none",
  text: "Login",
};
const defaultFormValues = {
  email: "",
  neverExpire: false,
  agreedToTermsAndConditions: false,
  password: "",
};
interface JSONObject {
  [key: string]: any;
}
const defaultFormErrors: JSONObject = {
  email: undefined,
  neverExpire: undefined,
  agreedToTermsAndConditions: undefined,
  password: undefined,
};

interface LoginFormProps {
  setSessionActive: React.Dispatch<React.SetStateAction<boolean>>;
}
const LoginForm: React.FC<LoginFormProps> = ({ setSessionActive }) => {
  const { values, handleChange } = formInput(defaultFormValues);
  // const [captchaToken, setCaptchaToken] = useState("");
  const [inputErrors, setInputErrors] = useState(defaultFormErrors);
  const [loginButton, setLoginButton] = useState(defaultLoginButtonParams);
  const [formFeedback, setFormFeedback] = useState<FeedbackParams>(
    defaultFeedbackParams
  );

  const changeLoginButton = (
    icon = defaultLoginButtonParams.icon,
    text = defaultLoginButtonParams.text
  ) => {
    setLoginButton({
      ...loginButton,
      icon,
      text,
    });
  };
  const changeFormFeedback = (
    icon = defaultFeedbackParams.icon,
    text = defaultFeedbackParams.text
  ) => {
    setFormFeedback({
      ...formFeedback,
      icon,
      text,
    });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    console.log("Got data:", values);
    let errorMsgs: JSONObject = { ...defaultFormErrors };

    //Note: Make all necessary checks before checking for blank fields

    //Check if the user accepted the terms and conditions
    if (!values.agreedToTermsAndConditions)
      errorMsgs.agreedToTermsAndConditions =
        "You need to agree to the terms and conditions";

    //If previous checks pass, verify if any field was left blank
    Object.keys(values).forEach((inputFieldName) => {
      if (values[inputFieldName] === "") {
        errorMsgs[inputFieldName] = "This field cannot be blank";
      }
    });

    setInputErrors(errorMsgs);

    let noEmptyFields = Object.keys(errorMsgs)
      .map((value) => errorMsgs[value] === undefined)
      .every((v) => v === true);

    console.log("Is there any field still empty?", errorMsgs);
    if (!noEmptyFields) return; //Stop execution if there are empty fields

    changeLoginButton("loading", "Loging");

    console.log("Submited: ", values);
    fetch("/api/v1/account/auth/login", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ...values,
        // reCaptcha: captchaToken,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        console.log(data);
        if (data.success === true) {
          changeLoginButton("success", "Logged in!");
          changeFormFeedback("success", "Logged in! Redirecting...");
          setSessionActive(true);
          if (window.electron?.ipcRenderer?.sendMessage !== undefined)
            window.electron?.ipcRenderer?.sendMessage("account-setToken", []);
        } else {
          changeLoginButton(); //Set default options
          changeFormFeedback("error", `Could not login: ${data.reason}`);
          // window.grecaptcha.reset();
        }
      });
  };


  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Open Rehab Initiative Dashboard
        </Typography>
        <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                id="email"
                name="email"
                label="Email Address or Username"
                placeholder="email@example.org"
                autoComplete="email"
                value={values.email}
                onChange={handleChange}
                variant='outlined'
                error={inputErrors.email !== undefined}
                helperText={inputErrors.email}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="password"
                value={values.password}
                onChange={handleChange}
                variant='outlined'
                error={inputErrors.password !== undefined}
                helperText={inputErrors.password}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <CustomInput
                type="checkbox"
                onChange={handleChange}
                name="agreedToTermsAndConditions"
                label={
                  <>
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    I agree with <a href="#">terms</a> and <a href="#">conditions</a>
                  </>
                }
                value={values.agreedToTermsAndConditions}
                errorMessage={inputErrors.agreedToTermsAndConditions}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomInput
                type="checkbox"
                onChange={handleChange}
                name="neverExpire"
                label={<>Disable session expiration</>}
                value={values.neverExpire}
                errorMessage={inputErrors.neverExpire}
              />
            </Grid>
            {/**<Grid item xs={12}>
                  <Captcha onChange={(token => {
                    if (token) setCaptchaToken(token);
                  })} />
                </Grid>**/}

          </Grid>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
          >
            Login
          </Button>
          <Grid container justifyContent="flex-end">
            <Grid item>
              <Link href="/authentication/register" variant="body2">
                Need an account? Register here
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Box>
      {formFeedback.icon !== "none" && (
        <Alert severity={formFeedback.icon}>{formFeedback.text}</Alert>
      )}
      <Copyright sx={{ mt: 5 }} />
    </Container>
  );

};

export default LoginForm;
